import { Injectable } from '@angular/core';
import { AuthenticationMethod, IntegrationModel, Platform, ResourceType } from 'src/app/models/api';
import { ActionTypes } from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { PlaybookSupportedActions } from 'src/app/models/componentInputs/playbookActionInput.interface';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { IActionTypeResponse } from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { arePlatformActionsAllowed } from '../../common/actionsHelpers';
import { PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { NewViolationFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newViolationTriggerModel';
import { NewResourceFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newResourceTriggerModel';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { BaseViolationRule } from 'src/app/models/api/governance/violationRule.model';
import _ from 'lodash';
import { isResourceTrigger, isViolationTrigger, isFindingsTrigger, isIssueTrigger } from 'src/app/common/playbookUtils';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';

@Injectable({
  providedIn: 'root',
})
export class PlaybooksTriggerActionsService {
  private _baseViolationRules: BaseViolationRule[] = [];

  constructor(
    private readonly _store: StoreService,
    private readonly _permissionsService: PermissionsService,
    private readonly _labelsProvider: LabelsProviderPipe,
    private readonly _logger: LoggerService,
    private readonly _featureFlagsService: FeatureFlagsService
  ) {
    this._store.baseViolationRule$.subscribe((rules) => (this._baseViolationRules = rules));
  }

  private _getGeneralActions(trigger?: PlaybookTrigger): ActionTypes[] {
    const actions = [ActionTypes.wait, ActionTypes.sendEmail, ActionTypes.sendWebhook];
    // sendToSoc is not supported for resource triggers (no API support)
    if (
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.EnableSendToSoc) &&
      !(trigger && isResourceTrigger(trigger.triggerType)) &&
      !(trigger && isIssueTrigger(trigger.triggerType))
    ) {
      actions.push(ActionTypes.sendToSoc);
    }
    return actions;
  }

  public setActionsDisabledAndTooltip(
    integration: IntegrationModel,
    actions: IActionTypeResponse[],
    trigger?: PlaybookTrigger
  ): Set<PlaybookSupportedActions> {
    let supportedActions: PlaybookSupportedActions[] = [];

    actions.forEach((action) => {
      const actionType = action.actionType;

      if (trigger && isFindingsTrigger(trigger.triggerType)) {
        const allowedActions = [
          ActionTypes.sendWebhook,
          ActionTypes.sendEmail,
          ActionTypes.deleteAgentThread,
          ActionTypes.isolateGpt,
          ActionTypes.addLabel,
        ];
        if (this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.EnableSendToSoc)) {
          allowedActions.push(ActionTypes.sendToSoc);
        }
        if (!allowedActions.includes(actionType)) {
          return;
        }
      }

      if (trigger && isIssueTrigger(trigger.triggerType)) {
        const allowedActions = [ActionTypes.sendWebhook, ActionTypes.sendEmail, ActionTypes.wait];
        if (!allowedActions.includes(actionType)) {
          return;
        }
      }

      const tooltip = this._getActionTooltip(actionType, action.restrictedReason);
      const supportedAction: PlaybookSupportedActions = {
        actionType: actionType,
        disabled: action.isRestricted,
        tooltip: tooltip !== '' ? tooltip : undefined,
      };
      if (integration.platform == Platform.workato) {
        this._handleWorkatoActions(supportedAction, integration.authenticationMethod);
      }
      supportedActions.push(supportedAction);
    });

    supportedActions = this._substituteTwoActionsWithOneWorkaround(supportedActions);

    // Add general actions that should always be available
    this._getGeneralActions(trigger).forEach((generalAction) => {
      if (!supportedActions.some((action) => action.actionType === generalAction)) {
        supportedActions.push({
          actionType: generalAction,
          disabled: false,
          tooltip: undefined,
        });
      }
    });

    return new Set(supportedActions);
  }

  public getTriggerResourceTypes(trigger: PlaybookTrigger): Set<ResourceType> {
    if (isResourceTrigger(trigger.triggerType)) {
      const configuration = trigger.configuration as NewResourceFoundTriggerConfigModel;
      if (configuration.resourceTypes) {
        return new Set(configuration.resourceTypes);
      } else return new Set();
    } else if (isViolationTrigger(trigger.triggerType)) {
      const configuration = trigger.configuration as NewViolationFoundTriggerConfigModel;
      const foundResourceTypes: Set<ResourceType> = new Set<ResourceType>();
      if (configuration.ruleIds) {
        const baseViolationRulesById = _.keyBy(this._baseViolationRules, 'id');
        const rulesIdsNotInBaseViolationRules = configuration.ruleIds.filter(
          (ruleId) => !baseViolationRulesById[ruleId]
        );
        if (rulesIdsNotInBaseViolationRules.length > 0) {
          this._logger.error(`Playbook contains deleted rules ${rulesIdsNotInBaseViolationRules.join(', ')}`);
        }

        configuration.ruleIds
          .filter((ruleId) => baseViolationRulesById[ruleId])
          .forEach((ruleId) => {
            const rule = baseViolationRulesById[ruleId];
            foundResourceTypes.add(rule.resourceType);
          });
        return foundResourceTypes;
      } else return foundResourceTypes;
    } else if (isFindingsTrigger(trigger.triggerType)) {
      return new Set<ResourceType>();
    } else if (isIssueTrigger(trigger.triggerType)) {
      return new Set<ResourceType>();
    }
    throw new Error(`PlaybooksTriggerActionsService: Missing support for ${trigger.triggerType} trigger`);
  }

  public getRulesByIds(ruleIds: Set<string>): BaseViolationRule[] {
    return this._baseViolationRules.filter((rule) => ruleIds.has(rule.id));
  }

  private _getActionTooltip(actionType: ActionTypes, restrictedReason: string): string {
    switch (actionType) {
      case ActionTypes.wait:
        return this._labelsProvider.transform('playbooks.waitActionTooltip');
    }
    return restrictedReason ? restrictedReason.replace(/,(?!\s)/g, ', ') : restrictedReason;
  }

  private _handleWorkatoActions(supportedAction: PlaybookSupportedActions, authMethod: AuthenticationMethod): void {
    if (this._getGeneralActions().includes(supportedAction.actionType)) return;
    const workatoActionsAllowed = arePlatformActionsAllowed(Platform.workato, authMethod);
    const disabled = !workatoActionsAllowed || supportedAction.disabled ? true : false;
    const tooltip = !workatoActionsAllowed
      ? this._labelsProvider.transform('playbooks.apiAction')
      : supportedAction.tooltip;
    supportedAction.disabled = disabled;
    supportedAction.tooltip = tooltip;
  }

  private _substituteTwoActionsWithOneWorkaround(
    supportedActions: PlaybookSupportedActions[]
  ): PlaybookSupportedActions[] {
    // Temp colution to avoid showing "Enable Agent Action" and "Disable Agent Action" in the menu while BE sends 2 options instead of 1
    if (
      supportedActions.some(
        (action: PlaybookSupportedActions) =>
          action.actionType === ActionTypes.enableAgentAction || action.actionType === ActionTypes.disableAgentAction
      )
    ) {
      supportedActions = supportedActions.filter(
        (action) =>
          action.actionType !== ActionTypes.enableAgentAction && action.actionType !== ActionTypes.disableAgentAction
      );

      supportedActions.push({
        actionType: ActionTypes.setActionStatus,
        disabled: false,
        tooltip: undefined,
      });
    }

    return supportedActions;
  }
}
